import React from "react";

/**
 * LegalPage: Terms of Service and Privacy Policy
 * Static legal content page
 */
const LegalPage: React.FC = () => {
  return (
    <div className="min-h-screen py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="heading-1 mb-4">Terms and Conditions & Privacy Policy</h1>
          <p className="body-large text-gray-600">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-8 md:p-12">
          <div className="prose prose-lg max-w-none">
            <div className="space-y-8">
              
              <section>
                <h2 className="heading-3 text-primary-600 mb-4">I. Introduction</h2>
                <p className="body-medium">
                  ReadyToClaim is a service and software platform designed to help homeowners securely document their personal belongings in preparation for potential natural disasters or emergencies. This document outlines our legal terms and disclaimers to inform and protect our users.
                </p>
              </section>

              <section>
                <h2 className="heading-3 text-primary-600 mb-4">II. Consent and Privacy Policy</h2>
                <p className="body-medium">
                  By using ReadyToClaim, you consent to our collection and storage of photographic and inventory data. We prioritize privacy and use encryption and secure login methods to protect user data. While we do not share any submitted content with third parties, no method of electronic storage or transmission can be guaranteed to be 100% secure.
                </p>
              </section>

              <section>
                <h2 className="heading-3 text-primary-600 mb-4">III. Data Handling and Technology Usage</h2>
                <p className="body-medium">
                  ReadyToClaim uses reverse image search technology to organize, tag, and structure your inventory data. This system improves speed and accuracy but does not replace professional valuation or legal advice. All technology processes are automated and reviewed by the specialist and user.
                </p>
              </section>

              <section>
                <h2 className="heading-3 text-primary-600 mb-4">IV. No Guarantee of Insurance Outcomes</h2>
                <p className="body-medium">
                  ReadyToClaim does not guarantee compensation or claim approval from any insurance provider. Our service is a preparation tool only. Decisions on claims are made solely by your insurance adjuster and company.
                </p>
              </section>

              <section>
                <h2 className="heading-3 text-primary-600 mb-4">V. Limitation of Liability</h2>
                <p className="body-medium">
                  ReadyToClaim shall not be held responsible for any loss, direct or indirect, due to use or misuse of the service, including but not limited to data breaches, system outages, or insurance claim rejections. Users assume all risks associated with documenting and storing property information digitally.
                </p>
              </section>

              <section>
                <h2 className="heading-3 text-primary-600 mb-4">VI. Authentication & Privacy Policy</h2>
                <p className="body-medium">
                  Users must register using a valid email and password. Email verification is required before account activation to ensure the accuracy of the provided information. Password reset functionality is provided via a secure email link, which expires within 24 hours of issuance. Users are solely responsible for maintaining the confidentiality and security of their login credentials to protect their accounts from unauthorized access.
                </p>
              </section>

              <section>
                <h2 className="heading-3 text-primary-600 mb-4">VII. Data Storage and Processing</h2>
                <p className="body-medium">
                  All user account and inventory data is securely stored on Supabase's cloud platform. Supabase serves as our provider for authentication, database, and email services. All data handled through the platform is encrypted both in transit and at rest to safeguard privacy and prevent unauthorized access. Supabase's privacy practices can be reviewed at <a href="https://supabase.com/privacy" className="text-primary-600 hover:text-primary-700 underline">https://supabase.com/privacy</a>.
                </p>
              </section>

              <section>
                <h2 className="heading-3 text-primary-600 mb-4">VIII. Data Access</h2>
                <p className="body-medium">
                  Customers can only access their own inventory data through the ReadyToClaim system. However, ReadyToClaim specialists have access to all inventory data and have the ability to assign items to other users as needed for system functionality. All data access actions are logged and monitored to ensure accountability and transparency.
                </p>
              </section>

              <section>
                <h2 className="heading-3 text-primary-600 mb-4">IX. Data Protection</h2>
                <p className="body-medium">
                  User data is encrypted and stored in a secure manner using industry-standard practices. Data is only shared when necessary for the proper functioning of the system, such as for assigning items or managing authentication processes. Users have the right to request deletion of their account and data at any time. ReadyToClaim adheres to all applicable data protection laws to ensure compliance and safeguard user rights.
                </p>
              </section>

              <section>
                <h2 className="heading-3 text-primary-600 mb-4">X. Third-Party Services</h2>
                <p className="body-medium">
                  ReadyToClaim relies on Supabase to provide essential services including authentication, database management, and email functionality. The handling of your data by Supabase is governed by their own privacy policy, which outlines how your information is collected, used, and protected. Users with data-related concerns may contact Supabase directly: <a href="mailto:privacy@supabase.com" className="text-primary-600 hover:text-primary-700 underline">privacy@supabase.com</a>
                </p>
              </section>

              <section>
                <h2 className="heading-3 text-primary-600 mb-4">XI. Terms Modifications and Contact</h2>
                <p className="body-medium">
                  We reserve the right to update or modify these terms at any time. Continued use of the platform constitutes acceptance of any changes. For questions or concerns, please contact our support team via the information provided on our official website.
                </p>
                <p className="body-medium mt-4">
                  By going forward with this service, you understand and acknowledge all possibilities, including but not limited to limitations of data privacy, insurance outcome, and ReadyToClaim's liability. If you have any questions or concerns, please feel free to email <a href="mailto:info@readytoclaim.com" className="text-primary-600 hover:text-primary-700 underline">info@readytoclaim.com</a>
                </p>
              </section>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPage; 